/**
 * PropertiesPanel — Right panel of the Visual Workspace Designer.
 *
 * Context-sensitive: shows properties for the currently selected element
 * (workspace, view, panel, column, filter, etc.)
 */
import React, { useCallback, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Autocomplete,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import ExtensionIcon from '@mui/icons-material/Extension';
import LinkOffIcon from '@mui/icons-material/LinkOff';

import {
  useDesignerState,
  useDesignerDispatch,
  useSelectedView,
  useSelectedPanel,
} from './DesignerStateContext';

// ---------------------------------------------------------------------------
// Property field wrappers
// ---------------------------------------------------------------------------
function PropTextField({ label, value, onChange, colors, ...rest }) {
  return (
    <TextField
      label={label}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      fullWidth
      sx={{
        '& .MuiOutlinedInput-root': {
          color: colors?.text || '#e0e0e0',
          fontSize: '0.8rem',
          '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
          '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
          '&.Mui-focused fieldset': { borderColor: '#22c55e' },
        },
        '& .MuiInputLabel-root': {
          color: colors?.textSecondary || '#888',
          fontSize: '0.8rem',
          '&.Mui-focused': { color: '#22c55e' },
        },
      }}
      {...rest}
    />
  );
}

function PropSelect({ label, value, onChange, options, colors }) {
  return (
    <FormControl fullWidth size="small">
      <InputLabel
        sx={{
          color: colors?.textSecondary || '#888',
          fontSize: '0.8rem',
          '&.Mui-focused': { color: '#22c55e' },
        }}
      >
        {label}
      </InputLabel>
      <Select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        label={label}
        sx={{
          color: colors?.text || '#e0e0e0',
          fontSize: '0.8rem',
          '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
          '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
          '&.Mui-focused fieldset': { borderColor: '#22c55e' },
        }}
      >
        {options.map((opt) => (
          <MenuItem key={typeof opt === 'string' ? opt : opt.value} value={typeof opt === 'string' ? opt : opt.value}>
            {typeof opt === 'string' ? opt : opt.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function PropSwitch({ label, checked, onChange, colors }) {
  return (
    <FormControlLabel
      control={
        <Switch
          checked={!!checked}
          onChange={(e) => onChange(e.target.checked)}
          size="small"
          sx={{ '& .Mui-checked': { color: '#22c55e' }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: '#22c55e' } }}
        />
      }
      label={<Typography sx={{ fontSize: '0.8rem', color: colors?.text || '#e0e0e0' }}>{label}</Typography>}
    />
  );
}

function SectionHeader({ title, colors }) {
  return (
    <Typography
      variant="overline"
      sx={{ color: colors?.textSecondary || '#888', fontSize: '0.65rem', letterSpacing: 1, mt: 1.5, mb: 0.5, display: 'block' }}
    >
      {title}
    </Typography>
  );
}

// ---------------------------------------------------------------------------
// Workspace Properties
// ---------------------------------------------------------------------------
function WorkspacePropertiesForm({ colors }) {
  const designer = useDesignerState();
  const dispatch = useDesignerDispatch();
  const { workspace } = designer;

  const update = useCallback(
    (key, value) => dispatch({ type: 'UPDATE_WORKSPACE_PROPS', props: { [key]: value } }),
    [dispatch]
  );

  const ICONS = [
    'ViewModule', 'Settings', 'CallReceived', 'Inventory2', 'LocalShipping',
    'Store', 'Assessment', 'Build', 'Dashboard', 'Category',
    'Description', 'ShoppingCart', 'Warehouse', 'ReceiptLong',
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <SectionHeader title="Workspace Properties" colors={colors} />
      <PropTextField label="Title" value={workspace.title} onChange={(v) => update('title', v)} colors={colors} />
      <PropSelect
        label="Icon"
        value={workspace.icon}
        onChange={(v) => update('icon', v)}
        options={ICONS}
        colors={colors}
      />
      <PropTextField label="Domain" value={workspace.domain} onChange={(v) => update('domain', v)} colors={colors} />
      <PropSelect
        label="Access Level"
        value={workspace.accessLevel}
        onChange={(v) => update('accessLevel', v)}
        options={['USER', 'ADMIN', 'SYSADMIN']}
        colors={colors}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// View Properties
// ---------------------------------------------------------------------------
function ViewPropertiesForm({ colors }) {
  const selectedView = useSelectedView();
  const dispatch = useDesignerDispatch();

  const update = useCallback(
    (key, value) => {
      if (selectedView) dispatch({ type: 'UPDATE_VIEW', viewId: selectedView.viewId, props: { [key]: value } });
    },
    [dispatch, selectedView]
  );

  if (!selectedView) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <SectionHeader title="View Properties" colors={colors} />
      <PropTextField label="Label" value={selectedView.label} onChange={(v) => update('label', v)} colors={colors} />
      <PropSwitch
        label="Default view"
        checked={selectedView.isDefault}
        onChange={() => dispatch({ type: 'SET_DEFAULT_VIEW', viewId: selectedView.viewId })}
        colors={colors}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Shared hook — fetch entities from catalog
// ---------------------------------------------------------------------------
function useEntities() {
  const [entities, setEntities] = useState([]);
  useEffect(() => {
    const token = localStorage.getItem('waide_access_token');
    const headers = { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) };
    fetch('/api/v1/catalog/entities', { headers })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setEntities(Array.isArray(data) ? data.map((e) => e.entityName || e.name || e) : []))
      .catch(() => setEntities([]));
  }, []);
  return entities;
}

// ---------------------------------------------------------------------------
// Shared hook — fetch fields for an entity, optionally filtered by fieldType
// ---------------------------------------------------------------------------
function useEntityFields(entityName) {
  const [fields, setFields] = useState([]);
  useEffect(() => {
    if (!entityName) { setFields([]); return; }
    const token = localStorage.getItem('waide_access_token');
    const headers = { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) };
    fetch(`/api/v1/catalog/${entityName}/fields`, { headers })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setFields(Array.isArray(data) ? data : []))
      .catch(() => setFields([]));
  }, [entityName]);
  return fields;
}

// ---------------------------------------------------------------------------
// Entity picker dropdown
// ---------------------------------------------------------------------------
function EntityPicker({ value, onChange, colors }) {
  const entities = useEntities();
  return (
    <Autocomplete
      size="small"
      options={entities}
      value={value || ''}
      onChange={(_, val) => onChange(val || '')}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Entity"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              color: colors?.text || '#e0e0e0',
              fontSize: '0.8rem',
              '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
              '&.Mui-focused fieldset': { borderColor: '#22c55e' },
            },
            '& .MuiInputLabel-root': {
              color: colors?.textSecondary || '#888',
              fontSize: '0.8rem',
              '&.Mui-focused': { color: '#22c55e' },
            },
          }}
        />
      )}
      sx={{
        '& .MuiAutocomplete-popupIndicator': { color: colors?.textSecondary || '#666' },
        '& .MuiAutocomplete-clearIndicator': { color: colors?.textSecondary || '#666' },
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Column config row — field picker with type filter + create new
// ---------------------------------------------------------------------------
function ColumnConfigRow({ col, index, panelEntity, panelId, dispatch, colors }) {
  const allFields = useEntityFields(panelEntity);
  const [showCreate, setShowCreate] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');

  // Filter fields by the column's fieldType if set
  const filteredFields = col.fieldType
    ? allFields.filter((f) => f.fieldType === col.fieldType)
    : allFields;

  const fieldOptions = filteredFields.map((f) => f.fieldName);

  const handleFieldChange = (fieldName) => {
    dispatch({ type: 'UPDATE_COLUMN', panelId, index, props: { fieldName } });
  };

  const handleCreateField = () => {
    if (newFieldName.trim()) {
      dispatch({ type: 'UPDATE_COLUMN', panelId, index, props: { fieldName: newFieldName.trim() } });
      setShowCreate(false);
      setNewFieldName('');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 0.5, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Chip
          label={col.fieldType || '?'}
          size="small"
          sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(34,197,94,0.15)', color: '#22c55e' }}
        />
        <Autocomplete
          size="small"
          options={fieldOptions}
          value={col.fieldName || ''}
          onChange={(_, val) => handleFieldChange(val || '')}
          freeSolo
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={panelEntity ? 'Select field...' : 'Set entity first'}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: colors?.text || '#e0e0e0',
                  fontSize: '0.7rem',
                  py: 0,
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                },
                '& .MuiInputBase-input': { py: '2px !important' },
              }}
            />
          )}
          sx={{ flex: 1, '& .MuiAutocomplete-popupIndicator': { p: 0 } }}
          disabled={!panelEntity}
        />
        <Tooltip title="Create new field">
          <IconButton
            size="small"
            onClick={() => setShowCreate(!showCreate)}
            sx={{ p: 0.25, color: colors?.textSecondary || '#666' }}
          >
            <AddIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Remove column">
          <IconButton
            size="small"
            onClick={() => dispatch({ type: 'REMOVE_COLUMN', panelId, index })}
            sx={{ p: 0.25, color: colors?.textSecondary || '#666' }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
      {showCreate && (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="New field name"
            value={newFieldName}
            onChange={(e) => setNewFieldName(e.target.value)}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                color: colors?.text || '#e0e0e0',
                fontSize: '0.7rem',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
              },
              '& .MuiInputBase-input': { py: '3px' },
            }}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateField()}
          />
          <Button size="small" variant="outlined" onClick={handleCreateField} sx={{ fontSize: '0.65rem', minWidth: 40, py: 0 }}>
            Add
          </Button>
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Grid Panel Properties
// ---------------------------------------------------------------------------
function GridPanelPropertiesForm({ colors }) {
  const panel = useSelectedPanel();
  const designer = useDesignerState();
  const dispatch = useDesignerDispatch();

  const viewId = designer.selection.viewId || designer.views[0]?.viewId;

  const update = useCallback(
    (key, value) => {
      if (panel) dispatch({ type: 'UPDATE_PANEL', viewId, panelId: panel.panelId, props: { [key]: value } });
    },
    [dispatch, viewId, panel]
  );

  if (!panel || (panel.type !== 'grid' && panel.type !== 'detail-grid')) return null;

  const isDetail = panel.type === 'detail-grid';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <SectionHeader title={isDetail ? 'Detail Grid Properties' : 'Grid Properties'} colors={colors} />
      {isDetail && (
        <PropTextField label="Label" value={panel.label} onChange={(v) => update('label', v)} colors={colors} />
      )}
      <EntityPicker value={panel.entity} onChange={(v) => update('entity', v)} colors={colors} />
      <PropTextField label="Function" value={panel.function} onChange={(v) => update('function', v)} colors={colors} />
      {!isDetail && (
        <>
          <PropTextField label="Primary Key" value={panel.primaryKey} onChange={(v) => update('primaryKey', v)} colors={colors} />
          <PropTextField label="Display Field" value={panel.displayField} onChange={(v) => update('displayField', v)} colors={colors} />
          <PropTextField label="Response Data Key" value={panel.responseDataKey} onChange={(v) => update('responseDataKey', v)} colors={colors} />
          <PropSwitch label="Selectable rows" checked={panel.selectable} onChange={(v) => update('selectable', v)} colors={colors} />
        </>
      )}
      {isDetail && (
        <>
          <PropTextField label="Parent Key" value={panel.parentKey} onChange={(v) => update('parentKey', v)} colors={colors} />
          <PropTextField label="Foreign Key" value={panel.foreignKey} onChange={(v) => update('foreignKey', v)} colors={colors} />
          <PropTextField label="Title Template" value={panel.titleTemplate} onChange={(v) => update('titleTemplate', v)} colors={colors} placeholder="e.g. Lines for {{receiptNumber}}" />
        </>
      )}

      {/* Columns */}
      <SectionHeader title="Columns" colors={colors} />
      {(panel.columns || []).length === 0 ? (
        <Typography variant="caption" sx={{ color: colors?.textSecondary || '#666', fontStyle: 'italic' }}>
          Drag field types from the palette to add columns
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {(panel.columns || []).map((col, i) => (
            <ColumnConfigRow
              key={`${col.fieldType}-${i}`}
              col={col}
              index={i}
              panelEntity={panel.entity}
              panelId={panel.panelId}
              dispatch={dispatch}
              colors={colors}
            />
          ))}
        </Box>
      )}

      {/* Empty state */}
      {!isDetail && (
        <>
          <SectionHeader title="Empty State" colors={colors} />
          <PropTextField
            label="Title"
            value={panel.emptyState?.title}
            onChange={(v) => update('emptyState', { ...panel.emptyState, title: v })}
            colors={colors}
          />
          <PropTextField
            label="Message"
            value={panel.emptyState?.message}
            onChange={(v) => update('emptyState', { ...panel.emptyState, message: v })}
            colors={colors}
          />
        </>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Form / KPI / Chart Properties (skeleton)
// ---------------------------------------------------------------------------
function GenericPanelPropertiesForm({ colors }) {
  const panel = useSelectedPanel();
  const designer = useDesignerState();
  const dispatch = useDesignerDispatch();

  const viewId = designer.selection.viewId || designer.views[0]?.viewId;

  const update = useCallback(
    (key, value) => {
      if (panel) dispatch({ type: 'UPDATE_PANEL', viewId, panelId: panel.panelId, props: { [key]: value } });
    },
    [dispatch, viewId, panel]
  );

  if (!panel || panel.type === 'grid' || panel.type === 'detail-grid') return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <SectionHeader title={`${panel.type.toUpperCase()} Panel Properties`} colors={colors} />
      {panel.type === 'form' && (
        <>
          <EntityPicker value={panel.entity} onChange={(v) => update('entity', v)} colors={colors} />
          <PropTextField label="Submit Script" value={panel.submitScript} onChange={(v) => update('submitScript', v)} colors={colors} />
        </>
      )}
      {panel.type === 'kpi' && (
        <PropTextField label="Metric Label" value={panel.metricLabel} onChange={(v) => update('metricLabel', v)} colors={colors} />
      )}
      {panel.type === 'chart' && (
        <>
          <PropSelect
            label="Chart Type"
            value={panel.chartType}
            onChange={(v) => update('chartType', v)}
            options={['bar', 'line', 'pie', 'donut']}
            colors={colors}
          />
          <PropTextField label="Title" value={panel.chartTitle} onChange={(v) => update('chartTitle', v)} colors={colors} />
        </>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Ref View Properties — component reference info + overrides
// ---------------------------------------------------------------------------
function RefViewPropertiesForm({ colors }) {
  const selectedView = useSelectedView();
  const dispatch = useDesignerDispatch();
  const [newOverrideKey, setNewOverrideKey] = useState('');
  const [newOverrideValue, setNewOverrideValue] = useState('');

  if (!selectedView || !selectedView.isRef) return null;

  const overrides = selectedView.overrides || {};

  const handleUpdateOverride = (key, value) => {
    dispatch({
      type: 'UPDATE_VIEW_OVERRIDES',
      viewId: selectedView.viewId,
      overrides: { [key]: value },
    });
  };

  const handleRemoveOverride = (key) => {
    const newOverrides = { ...overrides };
    delete newOverrides[key];
    dispatch({
      type: 'UPDATE_VIEW',
      viewId: selectedView.viewId,
      props: { overrides: newOverrides },
    });
  };

  const handleAddOverride = () => {
    if (newOverrideKey.trim()) {
      let parsedValue = newOverrideValue;
      // Try to parse JSON values (objects, arrays, booleans, numbers)
      try {
        parsedValue = JSON.parse(newOverrideValue);
      } catch {
        // Keep as string
      }
      dispatch({
        type: 'UPDATE_VIEW_OVERRIDES',
        viewId: selectedView.viewId,
        overrides: { [newOverrideKey.trim()]: parsedValue },
      });
      setNewOverrideKey('');
      setNewOverrideValue('');
    }
  };

  const handleDetach = () => {
    dispatch({ type: 'DETACH_REF', viewId: selectedView.viewId });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <SectionHeader title="Component Reference" colors={colors} />

      {/* Component info */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          bgcolor: 'rgba(96,165,250,0.06)',
          borderRadius: 1,
          border: '1px solid rgba(96,165,250,0.15)',
        }}
      >
        <ExtensionIcon sx={{ fontSize: 20, color: '#60a5fa' }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ color: colors?.text || '#e0e0e0', fontSize: '0.8rem', fontWeight: 600, fontFamily: 'monospace' }}>
            {selectedView.componentCode}
          </Typography>
          <Typography variant="caption" sx={{ color: colors?.textSecondary || '#888' }}>
            {selectedView.componentType || 'VIEW'}
          </Typography>
        </Box>
        <LinkIcon sx={{ fontSize: 16, color: '#60a5fa' }} />
      </Box>

      {/* View label (editable) */}
      <PropTextField
        label="Tab Label"
        value={selectedView.label}
        onChange={(v) => dispatch({ type: 'UPDATE_VIEW', viewId: selectedView.viewId, props: { label: v } })}
        colors={colors}
      />

      <PropSwitch
        label="Default view"
        checked={selectedView.isDefault}
        onChange={() => dispatch({ type: 'SET_DEFAULT_VIEW', viewId: selectedView.viewId })}
        colors={colors}
      />

      {/* Overrides */}
      <SectionHeader title="$overrides" colors={colors} />
      <Typography variant="caption" sx={{ color: colors?.textSecondary || '#666', fontSize: '0.65rem' }}>
        Override properties from the base component. These are shallow-merged on top of the component definition.
      </Typography>

      {Object.keys(overrides).length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {Object.entries(overrides).map(([key, value]) => (
            <Box key={key} sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
              <Chip
                label={key}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.65rem',
                  bgcolor: 'rgba(245,158,11,0.1)',
                  color: '#f59e0b',
                  border: '1px solid rgba(245,158,11,0.2)',
                  mt: 0.5,
                  flexShrink: 0,
                }}
              />
              <PropTextField
                label=""
                value={typeof value === 'object' ? JSON.stringify(value) : String(value)}
                onChange={(v) => {
                  let parsed = v;
                  try { parsed = JSON.parse(v); } catch { /* keep string */ }
                  handleUpdateOverride(key, parsed);
                }}
                colors={colors}
                size="small"
                multiline={typeof value === 'object'}
                maxRows={4}
              />
              <Tooltip title="Remove override">
                <IconButton
                  size="small"
                  onClick={() => handleRemoveOverride(key)}
                  sx={{ p: 0.25, color: colors?.textSecondary || '#666', mt: 0.5 }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="caption" sx={{ color: colors?.textSecondary || '#666', fontStyle: 'italic' }}>
          No overrides — using base component as-is
        </Typography>
      )}

      {/* Add new override */}
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end' }}>
        <PropTextField
          label="Key"
          value={newOverrideKey}
          onChange={setNewOverrideKey}
          colors={colors}
          sx={{ flex: 1 }}
          onKeyDown={(e) => e.key === 'Enter' && handleAddOverride()}
        />
        <PropTextField
          label="Value"
          value={newOverrideValue}
          onChange={setNewOverrideValue}
          colors={colors}
          sx={{ flex: 1 }}
          onKeyDown={(e) => e.key === 'Enter' && handleAddOverride()}
        />
        <Tooltip title="Add override">
          <IconButton
            size="small"
            onClick={handleAddOverride}
            sx={{ color: '#f59e0b', p: 0.5 }}
          >
            <AddIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ borderColor: colors?.border || '#333', my: 0.5 }} />

      {/* Detach from component */}
      <Button
        size="small"
        startIcon={<LinkOffIcon sx={{ fontSize: 14 }} />}
        onClick={handleDetach}
        sx={{
          color: colors?.textSecondary || '#888',
          textTransform: 'none',
          fontSize: '0.75rem',
          justifyContent: 'flex-start',
          '&:hover': { color: '#ef4444', bgcolor: 'rgba(239,68,68,0.08)' },
        }}
      >
        Detach from component (make inline)
      </Button>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// PropertiesPanel (main)
// ---------------------------------------------------------------------------
export default function PropertiesPanel({ colors }) {
  const designer = useDesignerState();
  const selectedView = useSelectedView();
  const { selection } = designer;

  const isRefView = selectedView?.isRef;

  return (
    <Box
      sx={{
        width: 300,
        minWidth: 300,
        borderLeft: `1px solid ${colors?.border || '#333'}`,
        bgcolor: colors?.surface || '#1a1a2e',
        overflow: 'auto',
        flexShrink: 0,
        p: 1.5,
      }}
    >
      {/* Always show workspace props */}
      <WorkspacePropertiesForm colors={colors} />

      <Divider sx={{ borderColor: colors?.border || '#333', my: 1.5 }} />

      {/* Ref view properties */}
      {selection.type !== 'workspace' && isRefView && <RefViewPropertiesForm colors={colors} />}

      {/* Inline view properties */}
      {selection.type !== 'workspace' && !isRefView && <ViewPropertiesForm colors={colors} />}

      {/* Panel properties when a panel is selected (only for inline views) */}
      {selection.panelId && !isRefView && (
        <>
          <Divider sx={{ borderColor: colors?.border || '#333', my: 1.5 }} />
          <GridPanelPropertiesForm colors={colors} />
          <GenericPanelPropertiesForm colors={colors} />
        </>
      )}
    </Box>
  );
}
